import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ONBOARDING_TOUR_STEPS, TOUR_GEOMETRY } from "@/constants/tour";
import { useOnboardingTourSeen } from "@/hooks/useOnboardingTourSeen";
import { useWalletTourDestination } from "@/hooks/useWalletTourDestination";
import type { AccountDeepLink } from "@/interfaces/accountNav";
import type { OnboardingTourStep, TourContextValue } from "@/interfaces/tour";
import { waitForElements } from "@/utils/dom-helper";
import { requiredTargets, walletDeepLink, walletTourStep } from "@/utils/tour-helper";

const DASHBOARD_PATH = "/dashboard";
const ACCOUNT_PATH = "/account";
/** OrgDetail tab key — each receiver validates it against the tabs the caller can see. */
const ACCOUNT_TAB_USERS = "users";

const TourContext = createContext<TourContextValue | null>(null);

/**
 * Drives the onboarding spotlight tour: current step, the navigation it needs, and the wait for
 * its targets. Renders nothing — `components/tour/SpotlightTour` draws the overlay.
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { markSeen } = useOnboardingTourSeen();
  const walletDestination = useWalletTourDestination();

  /**
   * The tour's steps with the wallet step aimed at the surface this organization actually has.
   * The step LIST is constant; only that one step's targets move, and only when the wallet is
   * unreachable — both real arrangements render the same panel, so the same ids light it.
   */
  const steps = useMemo<readonly OnboardingTourStep[]>(
    () =>
      ONBOARDING_TOUR_STEPS.map((step) =>
        step.destination === "account-wallets" ? walletTourStep(step, walletDestination) : step,
      ),
    [walletDestination],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isSettling, setIsSettling] = useState(false);
  const [startedFromWelcome, setStartedFromWelcome] = useState(false);
  const [dismissedWelcomeThisSession, setDismissedWelcomeThisSession] = useState(false);

  /** Every transition takes the next token; a slow `runStep` whose token went stale returns
   * instead of stomping newer state. */
  const runTokenRef = useRef(0);
  /** Source of truth for the current step: `next()` twice in one tick must not read stale state. */
  const stepIndexRef = useRef(0);
  /** Read inside async/stable callbacks, where the rendered `location` would be a stale capture. */
  const pathnameRef = useRef(location.pathname);
  /** Cancels the in-flight `waitForElements` — the token guards state, this stops the observer. */
  const runAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // A pending runStep must not touch state, or keep observing, after the provider goes away.
  useEffect(() => {
    return () => {
      runTokenRef.current += 1;
      runAbortRef.current?.abort();
      runAbortRef.current = null;
    };
  }, []);

  const navigateForStep = useCallback(
    (step: OnboardingTourStep) => {
      if (step.destination === "dashboard") {
        if (pathnameRef.current !== DASHBOARD_PATH) navigate(DASHBOARD_PATH);
        return;
      }
      if (step.destination === "account-users") {
        // `nonce` is required: /account -> /account re-renders the receivers without remounting,
        // so an identical target reads as "already applied" and the deep link no-ops (DIV-7761).
        const deepLink: AccountDeepLink = { tab: ACCOUNT_TAB_USERS, nonce: Date.now() };
        navigate(ACCOUNT_PATH, { state: deepLink });
        return;
      }
      if (step.destination === "account-wallets") {
        // Where the wallet lives depends on the organization, so the tab is resolved rather than
        // assumed: hard-coding "groups" asked a single-group client for a tab it does not have,
        // OrgDetail dropped the link, and the step waited out its whole timeout on a dimmed page.
        const deepLink: AccountDeepLink = {
          ...walletDeepLink(walletDestination),
          nonce: Date.now(),
        };
        navigate(ACCOUNT_PATH, { state: deepLink });
      }
    },
    [navigate, walletDestination],
  );

  /**
   * Navigate, then wait for the step's required targets. `isSettling` keeps the overlay up with no
   * hole meanwhile, so nothing is lit in the wrong place mid-navigation.
   */
  const runStep = useCallback(
    async (index: number, token: number, signal: AbortSignal) => {
      // `steps`, not the constant: the wallet step's targets are the resolved ones, so the wait
      // below is for the elements this organization will actually render.
      const step = steps[index];
      if (!step) return;

      setIsSettling(true);
      navigateForStep(step);

      await waitForElements(requiredTargets(step), TOUR_GEOMETRY.targetTimeoutMs, signal);
      if (runTokenRef.current !== token) return;

      setIsSettling(false);
    },
    [navigateForStep, steps],
  );

  const goToStep = useCallback(
    (index: number) => {
      const token = ++runTokenRef.current;
      runAbortRef.current?.abort();
      const controller = new AbortController();
      runAbortRef.current = controller;
      stepIndexRef.current = index;
      setStepIndex(index);
      void runStep(index, token, controller.signal);
    },
    [runStep],
  );

  const end = useCallback(() => {
    runTokenRef.current += 1;
    runAbortRef.current?.abort();
    runAbortRef.current = null;
    setIsOpen(false);
    setIsSettling(false);
    // Idempotent, swallows its own failures — a preference write must not break the exit.
    void markSeen();
    if (pathnameRef.current !== DASHBOARD_PATH) navigate(DASHBOARD_PATH);
  }, [markSeen, navigate]);

  const start = useCallback(
    (options?: { fromWelcome?: boolean }) => {
      setStartedFromWelcome(options?.fromWelcome === true);
      setIsOpen(true);
      goToStep(0);
    },
    [goToStep],
  );

  const next = useCallback(() => {
    const target = stepIndexRef.current + 1;
    if (target >= steps.length) {
      end();
      return;
    }
    goToStep(target);
  }, [goToStep, end, steps.length]);

  const back = useCallback(() => {
    const target = stepIndexRef.current - 1;
    if (target < 0) return;
    goToStep(target);
  }, [goToStep]);

  const markWelcomeDismissed = useCallback(() => {
    setDismissedWelcomeThisSession(true);
  }, []);

  const value = useMemo<TourContextValue>(
    () => ({
      isOpen,
      stepIndex,
      steps,
      isSettling,
      startedFromWelcome,
      dismissedWelcomeThisSession,
      start,
      next,
      back,
      end,
      markWelcomeDismissed,
    }),
    [
      isOpen,
      stepIndex,
      steps,
      isSettling,
      startedFromWelcome,
      dismissedWelcomeThisSession,
      start,
      next,
      back,
      end,
      markWelcomeDismissed,
    ],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within TourProvider");
  return ctx;
}
